/**
 * ScormPlayerFrame — React component that loads SCORM content in an iframe
 * and wires up the appropriate adapter (1.2 or 2004) for CMI persistence.
 *
 * Uses srcdoc + <base href> to keep the iframe same-origin with parent
 * while loading resources from the backend. This avoids cross-origin
 * SecurityErrors when SCORM content accesses window.parent.API.
 *
 * Usage:
 *   <ScormPlayerFrame
 *     launchConfig={launchConfig}
 *     sessionToken={sessionToken}
 *     learnerName="John Doe"
 *     learnerId="user-123"
 *     onComplete={(e) => console.log('Completed:', e)}
 *   />
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';

import type {
    ScormPlayerFrameProps,
    ScormAdapterConfig,
    ScormVersion,
    ScormCompletionEvent,
} from './types';
import { Scorm12Adapter } from './Scorm12Adapter';
import { Scorm2004Adapter } from './Scorm2004Adapter';
import { restoreCmi, getAbsoluteUrl, resolveScormPackageId } from './api';
import { getScormLaunchDocumentUrl, prepareScormSrcdoc } from './scormSrcdoc';
import { fetchScormLaunchHtml, useScormPackageAccess } from './scormAccess';
import { useScormTranslations } from './i18n';
import { ScormSubmitModal, ScormSubmittedBanner } from './ScormSubmitModal';

/** Create the correct adapter for the SCORM version. */
function createAdapter(
    version: ScormVersion,
    config: ScormAdapterConfig,
): Scorm12Adapter | Scorm2004Adapter {
    if (version === 'scorm-12') {
        return new Scorm12Adapter(config);
    }
    return new Scorm2004Adapter(config);
}

export function ScormPlayerFrame({
    launchConfig,
    sessionToken,
    learnerName,
    learnerId,
    initialCmiData,
    onComplete,
    onCommit,
    onError,
    className,
    height = '100%',
    language,
}: ScormPlayerFrameProps) {
    const t = useScormTranslations(language);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const adapterRef = useRef<Scorm12Adapter | Scorm2004Adapter | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [adapterReady, setAdapterReady] = useState(false);
    const [srcdoc, setSrcdoc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Submit confirmation modal state
    const [pendingCompletion, setPendingCompletion] = useState<ScormCompletionEvent | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const handleError = useCallback(
        (err: Error) => {
            setError(err.message);
            onError?.(err);
        },
        [onError],
    );

    // EAA-1644 WP5: the learner always has a session here, so access is requested with the
    // session variant; the answer is the server path (mode `server`) or a CDN URL whose
    // signed cookies the same response set (mode `cdn`).
    const access = useScormPackageAccess(
        {
            storedBaseUrl: getAbsoluteUrl(launchConfig.package_base_url),
            packageId: resolveScormPackageId(launchConfig.package_id, launchConfig.package_base_url),
            sessionId: launchConfig.session_id,
            sessionToken,
            toAbsolute: getAbsoluteUrl,
        },
        adapterReady,
    );
    const resolvedBase = access.resolved;

    useEffect(() => {
        if (!access.error) return;
        handleError(new Error(access.error));
        setIsLoading(false);
        // Report each access failure once; an inline onError must not re-fire it per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access.error]);

    useEffect(() => {
        let cancelled = false;

        async function setup() {
            try {
                // 1. Restore CMI data if not provided (session resume)
                let cmiData = initialCmiData;
                if (!cmiData) {
                    try {
                        const restored = await restoreCmi(
                            launchConfig.session_id,
                            sessionToken,
                        );
                        if (restored.found) {
                            cmiData = restored.cmi_data;
                        }
                    } catch {
                        // First launch — no saved data, continue with empty CMI
                    }
                }

                if (cancelled) return;

                // 2. Create and initialize adapter
                const adapterConfig: ScormAdapterConfig = {
                    version: launchConfig.scorm_version,
                    sessionId: launchConfig.session_id,
                    packageBaseUrl: getAbsoluteUrl(launchConfig.package_base_url),
                    launchUrl: launchConfig.launch_url,
                    endpoints: launchConfig.endpoints,
                    sessionToken,
                    learnerName,
                    learnerId,
                    initialCmiData: cmiData,
                    onComplete: (evt: ScormCompletionEvent) => {
                        // Show confirmation modal instead of calling onComplete directly
                        if (evt.completionStatus && evt.completionStatus !== 'not attempted') {
                            setPendingCompletion(evt);
                        } else {
                            onCompleteRef.current?.(evt);
                        }
                    },
                    onCommit,
                    onError: handleError,
                };

                const adapter = createAdapter(launchConfig.scorm_version, adapterConfig);
                await adapter.initialize();

                if (cancelled) {
                    adapter.destroy();
                    return;
                }

                adapterRef.current = adapter;

                // 3. Set window.API on parent BEFORE iframe loads
                const iframe = iframeRef.current;
                if (iframe) {
                    adapter.loadIntoFrame(iframe);
                } else {
                    (window as any).API = (adapter as any).api ?? (adapter as any);
                    (window as any).API_1484_11 = (window as any).API;
                }

                // Signal that adapter is ready — this triggers HTML fetch
                setAdapterReady(true);
            } catch (err) {
                if (!cancelled) {
                    handleError(
                        err instanceof Error ? err : new Error(String(err)),
                    );
                    setIsLoading(false);
                }
            }
        }

        setup();

        return () => {
            cancelled = true;
            if (adapterRef.current) {
                adapterRef.current.destroy();
                adapterRef.current = null;
            }
        };
    }, [launchConfig.session_id]); // Only re-init on session change

    // Fetch SCORM HTML and prepare srcdoc with <base href> for same-origin rendering.
    // Uses srcdoc so the iframe is same-origin with parent — adapter can inject
    // window.API directly, and the SCO's findAPI(window.parent) works.
    useEffect(() => {
        if (!adapterReady || !resolvedBase) return;
        let cancelled = false;

        const packageBaseUrl = resolvedBase.baseUrl;
        const fullUrl = getScormLaunchDocumentUrl(packageBaseUrl, launchConfig.launch_url);

        (async () => {
            try {
                let html: string;
                try {
                    // EAA-1644: a CDN base needs the signed cookies on this request.
                    html = await fetchScormLaunchHtml(fullUrl, resolvedBase.credentialed);
                } catch (fetchErr) {
                    const reason = fetchErr instanceof Error ? fetchErr.message : String(fetchErr);
                    throw new Error(`${reason}: Failed to load SCORM content`);
                }
                if (cancelled) return;

                setSrcdoc(prepareScormSrcdoc(html, packageBaseUrl, launchConfig.launch_url));
                setIsLoading(false);
            } catch (err) {
                if (!cancelled) {
                    handleError(err instanceof Error ? err : new Error(String(err)));
                    setIsLoading(false);
                }
            }
        })();

        return () => { cancelled = true; };
    }, [adapterReady, resolvedBase, launchConfig.launch_url]);

    const handleIframeLoad = useCallback(() => {
        // Re-inject API after iframe loads (srcdoc is same-origin)
        if (adapterRef.current && iframeRef.current) {
            adapterRef.current.loadIntoFrame(iframeRef.current);
        }
    }, []);

    return (
        <div
            className={`scorm-player-frame ${className ?? ''}`}
            style={{
                position: 'relative',
                width: '100%',
                height: typeof height === 'number' ? `${height}px` : height,
                minHeight: 400,
            }}
        >
            {isLoading && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 10,
                    }}
                >
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                border: '3px solid #e5e7eb',
                                borderTopColor: '#3b82f6',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite',
                                margin: '0 auto 8px',
                            }}
                        />
                        <span style={{ color: '#6b7280', fontSize: 14 }}>
                            {t.playerLoading}
                        </span>
                    </div>
                </div>
            )}

            {error && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fef2f2',
                        zIndex: 10,
                    }}
                >
                    <div style={{ textAlign: 'center', padding: 24, maxWidth: 400 }}>
                        <div style={{ color: '#ef4444', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                            {t.playerError}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: 14 }}>
                            {error}
                        </div>
                    </div>
                </div>
            )}

            {srcdoc && (
                <iframe
                    ref={iframeRef}
                    srcDoc={srcdoc}
                    onLoad={handleIframeLoad}
                    title={t.playerTitle}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: isLoading ? 'none' : 'block',
                    }}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                    allow="autoplay; fullscreen"
                />
            )}

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>

            {/* Submit confirmation modal */}
            {pendingCompletion && !submitted && (
                <ScormSubmitModal
                    data={{
                        status: pendingCompletion.completionStatus || pendingCompletion.successStatus,
                        score: pendingCompletion.scoreRaw ?? undefined,
                    }}
                    t={t}
                    onConfirm={() => {
                        onCompleteRef.current?.(pendingCompletion);
                        setSubmitted(true);
                        setPendingCompletion(null);
                    }}
                    onCancel={() => setPendingCompletion(null)}
                />
            )}

            {/* Submitted banner */}
            {submitted && <ScormSubmittedBanner t={t} />}
        </div>
    );
}
